import { produce } from 'immer';

import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { syncTextNode } from './syncTextNode';

/**
 * Applies a full text node snapshot: restores position, dimensions, style, and data,
 * then syncs text data to the server.
 */
export function applyTextNodeSnapshot(snapshot: AppNode) {
  const store = useEditCanvasStore.getState();
  // Restore position (also triggers updateNodes callback for position sync)
  store.moveNodes([{ id: snapshot.id, position: snapshot.position }]);
  // Restore dimensions, style, and data (replace, not merge)
  useEditCanvasStore.setState((state) => ({
    nodes: state.nodes.map((n) => {
      if (n.id !== snapshot.id) {
        return n;
      }

      return produce(n, (draft) => {
        for (const key of Object.keys(draft.data)) {
          if (!(key in snapshot.data)) {
            Reflect.deleteProperty(draft.data, key);
          }
        }

        Object.assign(draft.data, snapshot.data);
        draft.style = snapshot.style;
        // Clear measured so ReactFlow re-measures from the restored style;
        // otherwise xyflow's stale post-resize measured outranks style and
        // the node visually keeps its pre-undo size.
        draft.measured = undefined;
        draft.width = snapshot.width;
        draft.height = snapshot.height;
      });
    }),
  }));

  // Flush dimensions to server (position already queued by moveNodes above).
  const snapshotWidth = typeof snapshot.style?.width === 'number' ? snapshot.style.width : undefined;
  const snapshotHeight = typeof snapshot.style?.height === 'number' ? snapshot.style.height : undefined;

  if (snapshotWidth != null || snapshotHeight != null) {
    useEditCanvasStore.getState().updateNodes?.([
      {
        id: snapshot.id,
        ...(snapshotWidth != null && { width: snapshotWidth }),
        ...(snapshotHeight != null && { height: snapshotHeight }),
      },
    ]);
  }

  syncTextNode(snapshot.id);
}
